import firestar, {
   Actions,
   CollectionDescriptor,
   Root,
   Users as FUsers
} from "firestar";
import firebase from 'firebase/compat/app'
import CSConnect from '.'

interface ClaimsType extends FUsers.UserClaims {

}

export const RoleIdOptions = {
   'owner': 'Owner',
   'admin': 'Admin',
   'moderator': 'Moderator',
   'editor': 'Editor',
   'viewer': 'Viewer',
}

const RoleLevelOptions = {
   'casino': 'Casino',
   'global': 'Global'
}

type RoleLevelType = keyof typeof RoleLevelOptions
type RoleIdType = keyof typeof RoleIdOptions

export interface RoleType extends FUsers.Role { }

export interface UserType extends Root, Users.UserInfo {
   cardNumbers?: { [key: string]: string },
   token?: string | null
}

namespace Users {

   export type User = UserType
   export type UserInfo = firebase.UserInfo
   export type Claims = ClaimsType
   export type RoleId = RoleIdType
   export type RoleLevel = RoleLevelType
   export type Role = RoleType

   export function createUser(userId: string, userInfo?: firebase.UserInfo | null) {
      return Actions.writeDocument<UserType>(`users/${userId}`, {
         ...userInfo
      })
   }

   export function updateUser(userId: string, userInfo: Partial<User> | User) {
      return Actions.updateDocument<UserType>(`users/${userId}`, {
         ...userInfo
      })
   }

   export function deleteUser(userId: string) {
      return Actions.deleteDocument(`users/${userId}`)
   }

   export function getUser(userId: string) {
      return Actions.readDocument<User>(`users/${userId}`)
   }

   export function getUserInfoWithEmail(email: string) {
      return new Promise<UserInfo | null>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()

            const body = {
               email: email
            }

            const response = await fetch(baseUrl + '/getUserRecordWithEmail', {
               method: 'POST',
               body: JSON.stringify(body),
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            if (response.ok) {
               const userRecord = await response.json()
               resolve(getUserInfo(userRecord))
            } else {
               resolve(null)
            }
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getUserInfoWithPhoneNumber(phoneNumber: string) {
      return new Promise<UserInfo | null>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()

            const body = {
               phoneNumber: phoneNumber
            }

            const response = await fetch(baseUrl + '/getUserRecordWithPhoneNumber', {
               method: 'POST',
               body: JSON.stringify(body),
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            if (response.ok) {
               const userRecord = await response.json()
               resolve(getUserInfo(userRecord))
            } else {
               resolve(null)
            }
         } catch (error) {
            reject(error)
         }
      })
   }


   export function createUserAccount(
      phoneNumber: string,
      displayName: string,
      email: string,
      createFallbackEmail?: boolean,
      fallbackInfo?: {
         casinoId: string,
         cardNumber: string
      }
   ) {
      return new Promise<UserInfo>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()

            const body = {
               phoneNumber,
               displayName,
               email
            } as any

            body.createFallbackEmail = createFallbackEmail ?? false
            body.fallbackInfo = fallbackInfo ?? null

            const response = await fetch(baseUrl + '/createUserAccount', {
               method: 'POST',
               body: JSON.stringify(body),
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            if (response.ok) {
               const userRecord = await response.json()
               resolve(userRecord)
            } else if (response.status >= 400 && response.status < 500) {
               const userRecord = await response.json()
               throw Error(userRecord.message)
            } else {
               throw new Error(response.statusText);
            }

         } catch (error) {
            reject(error)
         }
      })
   }

   export function updateUserAccount(
      uid: string,
      userInfo: Partial<UserInfo>
   ) {
      return new Promise<UserInfo>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()

            const body = {
               uid,
               userInfo,
            } as any

            const response = await fetch(baseUrl + '/updateUserAccount', {
               method: 'POST',
               body: JSON.stringify(body),
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })

            const userRecord = await response.json()
            resolve(userRecord)

         } catch (error) {
            reject(error)
         }
      })
   }

   export function getUsers(descriptor?: CollectionDescriptor) {
      return Actions.readCollection<User>(`users`, descriptor)
   }

   export function syncCasinoList(userId: string) {

   }

   export function getUserInfo(user: UserInfo | User | null): UserInfo {
      return {
         displayName: user?.displayName ?? null,
         email: user?.email ?? null,
         phoneNumber: user?.phoneNumber ?? null,
         photoURL: user?.photoURL ?? null,
         providerId: user?.providerId ?? '',
         uid: user?.uid ?? ''
      }
   }

   export function getCurrentUserInfo(): UserInfo {
      const user = firestar.currentUser()
      return getUserInfo(user)
   }

   export function updateUserInfo(user: Partial<firebase.UserInfo>) {
      return firestar.currentUser()?.updateProfile({
         ...user,
      })
   }

   export function updateToken(userId: string, token?: string) {
      return Actions.updateDocument<User>(`users/${userId}`, {
         token: token ?? null
      })
   }

   export function udpateMembership(
      userId: string,
      casinoId: string,
      cardNumber: string
   ) {
      return new Promise<void>(async (resolve, reject) => {
         try {
            const userResponse = await getUser(userId)
            await updateUser(userId, {
               cardNumbers: {
                  ...userResponse?.memberships ?? {},
                  [casinoId]: cardNumber
               }
            })
            resolve()
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getClaims() {
      return new Promise<Claims>(async (resolve, reject) => {
         try {
            const auth = firestar.getAuth()
            const response = await auth().currentUser?.getIdTokenResult(true)
            const claims = response?.claims ?? {} as Claims
            resolve(claims)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getRolesForUser(userId: string) {
      return new Promise<Users.Role[]>(async (resolve, reject) => {

         // try {
         //    const baseUrl = CSConnect.getBaseUrl()

         //    const body = {
         //       userId: userId
         //    }

         //    const response = await fetch(baseUrl + '/getRolesForUser', {
         //       method: 'POST',
         //       body: JSON.stringify(body),
         //       headers: {
         //          'Content-Type': 'application/json',
         //          'Accept': '*/*',
         //       }
         //    })
         //    const responseJSON = await response.json() as Users.Role[]

         //    const casinoIds = responseJSON.filter(r => r.casinoId != null).map((r) => r.casinoId)

         //    const promises = casinoIds.map(
         //       (casinoId) => new Promise<Casinos.CasinoInfo | null>(async (resolve) => {
         //          try {
         //             const casinoResponse = await Casinos.getCasinoWithId(casinoId)
         //             const casinoInfo = Casinos.getCasinoInfo(casinoResponse)
         //             resolve(casinoInfo)
         //          } catch (error) {
         //             resolve(null) //resolving to continue operation
         //          }
         //       })
         //    )

         //    const promisesResponse = await Promise.all(promises)

         //    console.log('promises', promisesResponse)

         //    promisesResponse.forEach((casinoInfo, index) => {
         //       if (casinoInfo != null) {
         //          const user = responseJSON[index]
         //          user.casinoInfo = casinoInfo
         //       }
         //       return null
         //    })

         //    resolve(responseJSON)

         // } catch (error) {
         //    reject(error)
         // }
      })
   }

   export function generateUserToken(uid: string) {
      return new Promise<{
         token: string
      }>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()

            const body = {
               uid
            }

            const response = await fetch(baseUrl + '/generateUserToken', {
               method: 'POST',
               body: JSON.stringify(body),
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })

            const tokenResponse = await response.json()
            resolve(tokenResponse)

         } catch (error) {
            reject(error)
         }
      })
   }

   export function sendNotification(
      notification: {
         description: string,
         title: string,
         imageUrl?: string
      },
      data: { [key: string]: string },
      deviceTokens?: string[],
      topic?: string,
      condition?: string,
      additionalProps?: { [key: string]: string }
   ) {
      return new Promise<{
         response: {
            failureCount: number,
            successCount: number,
            responses: {
               success: boolean,
               error: {
                  code: string,
                  message: string
               },
               messageId: string
            }[]
         },
         payload: {
            data: any,
            notification: any
         }
      }>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()

            const body = {
               notification,
               data,
               deviceTokens,
               topic,
               condition,
               additionalProps
            }

            const response = await fetch(baseUrl + '/sendNotification', {
               method: 'POST',
               body: JSON.stringify(body),
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })

            const tokenResponse = await response.json()
            resolve(tokenResponse)

         } catch (error) {
            reject(error)
         }
      })
   }

}

export default Users